import moment from 'moment';
import {
  TYPE_BEAT_EVENT_ENUM,
  TYPE_RHYTHM_EVENT_ENUM,
  TYPE_SPECIAL_EVENT_ENUM,
} from '@/shared/config';
import { TBeatHrActiveTab } from '@/shared/model';

export { formatEventStrip } from '@/entities/event';

/**
 * Derives the five-second strip window centred on a bookmark's sample position. Quantises the centre epoch down to the sampling interval implied by samplingFrequency, then takes 2.5 seconds either side of it.
 *
 * @param center - Epoch milliseconds of the bookmark the window is centred on.
 * @param samplingFrequency - Samples per second of the ECG recording, used to quantise the centre.
 * @returns An object carrying the defaultStartStrip and defaultStopStrip moments.
 */
export const calculateStartStopStripMomentOfListBookmark = (
  center: any,
  samplingFrequency: number,
) => {
  const dividedNumber = 1000 / samplingFrequency;
  const epoch = Math.floor(center / dividedNumber) * dividedNumber;
  const baseStartStrip = moment(epoch);
  const startStrip = baseStartStrip.clone().subtract(2.5, 'seconds');
  const stopStrip = baseStartStrip.clone().add(2.5, 'seconds');
  return {
    defaultStartStrip: startStrip,
    defaultStopStrip: stopStrip,
  };
};

/**
 * Derives the one-minute strip window an ECG event preview should render. Branches on the event's start second: past 50 seconds it anchors to the startTimeEpoch minute and spans 70 seconds, before 10 seconds it anchors 10 seconds earlier and spans 70 seconds, otherwise it spans the whole minute.
 *
 * @param event - The rhythm event whose preview window is being derived, read for start and startTimeEpoch.
 * @returns An object carrying the startStrip and stopStrip moments bounding the preview.
 */
export const calculateStartStopStripMomentOfEvent = (event: any) => {
  let startStrip;
  let stopStrip;
  if (
    event?.start &&
    moment(event?.start).valueOf() > event.startTimeEpoch &&
    moment(event?.start).second() > 50
  ) {
    // *: End of ECG Viewer
    startStrip = moment(event?.startTimeEpoch).startOf('minutes');
    stopStrip = startStrip.clone().add(70, 'seconds');
  } else if (event?.start && moment(event?.start).second() < 10) {
    // *: Start of ECG Viewer
    startStrip = moment(event?.startTimeEpoch).startOf('minutes').subtract(10, 'seconds');
    stopStrip = startStrip.clone().add(70, 'seconds');
  } else {
    startStrip = moment(event?.startTimeEpoch).startOf('minutes');
    stopStrip = startStrip.clone().add(1, 'minutes');
  }
  return { startStrip, stopStrip };
};

/**
 * Translates the special-event-type selection of a rhythm or beat tab into the extra range criteria sent with the holter events query. Yields no criteria for an unset type and for ALL_HR_RANGES, a maxHr band for tachy, a minHr band for brady and a duration band for pause, each choosing between the sinus or short variant and the open-ended extreme range.
 *
 * @param type - The active rhythm, beat or HR tab whose special-event selection is read.
 * @param specialEventTypes - Map of tab to the special event type currently selected for it.
 * @returns The additional filter criteria for the selection, empty when none applies.
 */
export const generateFilterSpecialEventType = (
  type: TYPE_RHYTHM_EVENT_ENUM | TYPE_BEAT_EVENT_ENUM | TBeatHrActiveTab,
  specialEventTypes: any,
) => {
  if (!specialEventTypes[type]) {
    return {};
  }
  if (specialEventTypes[type] === TYPE_SPECIAL_EVENT_ENUM.ALL_HR_RANGES) {
    return {};
  }
  // confirmed with BE that these ranges are correct: > 121 mean >= 122
  if (type === TYPE_RHYTHM_EVENT_ENUM.TACHY) {
    return specialEventTypes[type] === TYPE_SPECIAL_EVENT_ENUM.SINUS_TACHY
      ? {
          maxHr: {
            from: 100,
            to: 121,
          },
        }
      : {
          maxHr: {
            from: 122,
          },
        };
  }
  if (type === TYPE_RHYTHM_EVENT_ENUM.BRADY) {
    return specialEventTypes[type] === TYPE_SPECIAL_EVENT_ENUM.SINUS_BRADY
      ? {
          minHr: {
            from: 43,
            to: 60,
          },
        }
      : {
          minHr: {
            to: 42,
          },
        };
  }
  if (type === TYPE_RHYTHM_EVENT_ENUM.PAUSE) {
    return specialEventTypes[type] === TYPE_SPECIAL_EVENT_ENUM.SHORT_PAUSE
      ? {
          duration: {
            // Set 2.45 because 2.45 will round 2.5 s
            from: 2.45,
            to: 2.94,
          },
        }
      : {
          duration: {
            from: 2.95,
          },
        };
  }
  return {};
};

/**
 * Maps an absolute event index onto the cache chunk holding it. Integer-divides startIndex by chunkSize.
 *
 * @param startIndex - Absolute index of the event being located.
 * @param chunkSize - Number of events stored per chunk.
 * @returns The zero-based index of the chunk containing that event.
 */
export const getCurrentChunk = (startIndex: number, chunkSize: number) =>
  Math.floor(startIndex / chunkSize);

// Get a slice of cached data
/**
 * Reads a window of cached holter events out of the chunk-keyed cache. Starts from the current chunk's array, appends the following chunk when the requested range crosses a chunk boundary, then slices using the indices rebased onto the chunk's own offset.
 *
 * @param data - The cache keyed by chunk name, each entry holding that chunk's events.
 * @param startIndex - Absolute index of the first event wanted.
 * @param endIndex - Absolute index just past the last event wanted.
 * @param currnetChunk - Index of the chunk holding startIndex.
 * @param chunkSize - Number of events stored per chunk.
 * @returns The cached events falling between startIndex and endIndex.
 */
export const getCachedDataSlice = (
  data: any,
  startIndex: number,
  endIndex: number,
  currnetChunk: number,
  chunkSize: number,
) => {
  let events = data?.[`chunk-${currnetChunk}`] || [];
  const sliceChunkIdx = currnetChunk * chunkSize;
  const chunks = Math.floor(endIndex / chunkSize) - Math.floor(startIndex / chunkSize);
  if (chunks > 0) {
    const nextChunk = data?.[`chunk-${currnetChunk + 1}`] || [];
    events = [...events, ...nextChunk];
  }
  return events.slice(startIndex - sliceChunkIdx, endIndex - sliceChunkIdx);
};
